/**
 * Inline style tokens for the Business Profile screens.
 *
 * WHY INLINE STYLES AND NOT TAILWIND CLASSES
 * ──────────────────────────────────────────
 * src/index.css in this repo is COMPILED Tailwind output committed as a source
 * file, not a Tailwind entry point. Any utility class not already present in
 * that snapshot produces no rule at all and fails silently — `font-bold`,
 * `uppercase` and `text-[10px]` are among the ones already known to be missing.
 * New styling therefore goes in inline `style={{...}}` driven by useTheme(), the
 * pattern YapTabGuidelinesModal, PromptComposer and YapDayTimer already use.
 *
 * Colours are lifted from the existing screens so these surfaces sit inside the
 * app rather than beside it: #F39C12 / #E74C3C is the light brand gradient,
 * #4478ea the dark accent, #f2deb3 / #1a1a1a the card grounds.
 */

export type ThemeName = 'light' | 'dark';

export function businessTokens(theme: ThemeName | string) {
  const dark = theme === 'dark';
  return {
    dark,
    /** Page ground. */
    bg: dark ? '#0D0D0D' : '#eddeb7',
    /** Card / panel ground, on top of the page. */
    surface: dark ? '#1a1a1a' : '#f2deb3',
    /** Input and inset ground. */
    inset: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    border: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    text: dark ? '#ffffff' : '#1a1a1a',
    textMuted: dark ? '#a0a0a0' : '#666666',
    /** The single accent colour for this surface in the current theme. */
    accent: dark ? '#4478ea' : '#ea580c',
    accentSoft: dark ? 'rgba(68,120,234,0.16)' : 'rgba(234,88,12,0.12)',
    /** Primary button fill. */
    buttonBg: dark
      ? 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)'
      : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
    buttonText: '#ffffff',
    danger: '#E74C3C',
    success: dark ? '#4ade80' : '#16a34a',
    warning: '#F39C12',
  };
}

export type BusinessTokens = ReturnType<typeof businessTokens>;

/** Status pill colouring, shared by My Business and the admin queue. */
export function statusPill(t: BusinessTokens, status: string): { bg: string; fg: string; label: string } {
  switch (status) {
    case 'pending':   return { bg: 'rgba(243,156,18,0.18)', fg: t.warning, label: 'In review' };
    case 'approved':  return { bg: 'rgba(22,163,74,0.18)',  fg: t.success, label: 'Approved' };
    case 'denied':    return { bg: 'rgba(231,76,60,0.18)',  fg: t.danger,  label: 'Declined' };
    case 'active':    return { bg: 'rgba(22,163,74,0.18)',  fg: t.success, label: 'Live' };
    case 'suspended': return { bg: 'rgba(231,76,60,0.18)',  fg: t.danger,  label: 'Suspended' };
    case 'paid':      return { bg: 'rgba(68,120,234,0.18)', fg: t.accent,  label: 'Paid' };
    case 'deleted':   return { bg: 'rgba(128,128,128,0.18)', fg: t.textMuted, label: 'Deleted' };
    default:          return { bg: t.inset, fg: t.textMuted, label: status };
  }
}
